import Portal from "@Portals/components/Portal";
import {useTranslation} from "@Translation/hooks/useTranslation";
import InputField from "@Features/input/inputField/components/InputField";
import React, {useState} from "react";
import PhoneField from "@Features/input/phoneField/components/PhoneField";
import {emptyPhoneCountryObject} from "@Features/input/phoneField/constants/emptyPhoneCountryObject";
import ReCAPTCHA from "react-google-recaptcha";
import TextAreaField from "@Features/input/textAreaField/components/TextAreaField";
import GbbTermsAndConditions
    from "@CheckOrder/components/MainSection/TermsAndConditions/Checkboxes/GbbTermsAndConditions";
import Checkbox from "@Generic/form/Checkbox";
import Row from "@Containers/components/Row";
import LoaderButton from "@Generic/loaders/LoaderButton";
import useSubmit from "@Pages/busRent/hooks/useSubmit";
import validatePhone from "@Features/input/phoneField/functions/validatePhone";
import validateEmail from "@User/functions/validateEmail";
import useTermsAndConditionsCheckboxData from "@Features/input/hooks/useTermsAndConditionsCheckboxData";
import Notification from "@Features/notification/components/Notification";
import MessageSent from "@Pages/busRent/components/MessageSent";
import UnexpectedError from "@Pages/busRent/components/UnexpectedError";
import useIsMobile from "@Hooks/selector/useIsMobile";
import useInputHandler from "@Features/input/hooks/useInputHandler";
import {isNotEmpty} from "@Array/isNotEmpty";

function BusRentContactForm() {
    const {
        title: titleLabel,
        describe_your_trip_details: describeYourTripDetailsLabel,
        send_inquiry: sendInquiryLabel,
        full_name_should_not_be_blank: fullNameShouldNotBeBlankLabel,
        we_need_your_email_to_get_back_to_you: weNeedYourEmailToGetBackToYouLabel,
        invalid_phone_number: invalidPhoneNumberLabel,
        invalid_phone_number_country: invalidPhoneNumberCountryLabel,
        please_describe_your_trip_details: pleaseDescribeYourTripDetailsLabel,
        you_must_accept: youMustAcceptLabel,
    } = useTranslation('bus_rental.contact_form')

    const isMobile = useIsMobile()

    const [responseStatus, setResponseStatus] = useState(null)
    const [checkForErrors, setCheckForErrors] = useState(false)

    const fullName = useInputHandler(checkForErrors, isNotEmpty)
    const email = useInputHandler(checkForErrors, validateEmail)
    const phone = useInputHandler(checkForErrors, validatePhone)
    const [phoneCountry, setPhoneCountry] = useState(emptyPhoneCountryObject)
    const message = useInputHandler(checkForErrors, isNotEmpty)
    const tac = useTermsAndConditionsCheckboxData(checkForErrors)

    const {
        recaptchaRef,
        setLoading,
        loading,
        errors,
    } = useSubmit({
        setResponseStatus,
        phone: phone.value,
        country: phoneCountry.name,
        email: email.value,
        message: message.value,
        fullName: fullName.value,
    })

    const submitContactForm = (e: React.SyntheticEvent) => {
        e.preventDefault()

        setCheckForErrors(true)
        const errors = [
            fullName.isError,
            email.isError,
            phone.isError,
            message.isError,
        ]
        const hasNoErrors = errors.every((error) => (error === false))

        if (hasNoErrors) {
            setLoading(true)
        }
    }

    return (
        <Portal id="bus-rent-contact-form-portal">
            <form onSubmit={submitContactForm}>
                <h2>{titleLabel}</h2>
                <InputField
                    componentClassname="input-field"
                    htmlId="fullName"
                    value={fullName.value}
                    label="Full name"
                    onChange={fullName.handleDataInput}
                    onBlur={fullName.handleFocusOutOfInputField}
                    showError={fullName.showError && fullName.isError}
                    errorMessage={fullNameShouldNotBeBlankLabel}
                    inputClassname={fullName.inputClassName}
                />
                <InputField
                    componentClassname="input-field"
                    htmlId="email"
                    value={email.value}
                    label="Email"
                    onChange={email.handleDataInput}
                    onBlur={email.handleFocusOutOfInputField}
                    showError={email.showError && email.isError}
                    errorMessage={weNeedYourEmailToGetBackToYouLabel}
                    inputClassname={email.inputClassName}
                />
                <PhoneField
                    htmlId="phone"
                    phoneNumber={phone.value}
                    setPhoneNumber={phone.setValue}
                    phoneCountryObject={phoneCountry}
                    setPhoneCountryObject={setPhoneCountry}
                    label="Phone"
                    onBlur={phone.handleFocusOutOfInputField}
                    loadingClass={phone.inputClassName}
                    showError={phone.showError && phone.isError}
                    errorMessage={invalidPhoneNumberLabel}
                />
                <TextAreaField
                    value={message.value}
                    onChange={message.handleDataInput}
                    onBlur={message.handleFocusOutOfInputField}
                    placeholder={describeYourTripDetailsLabel}
                    htmlId="textArea"
                    label="Message"
                    textAreaClassName={message.inputClassName}
                    limit={1000}
                    showError={message.showError && message.isError}
                    errorMessage={pleaseDescribeYourTripDetailsLabel}
                />
                <div className="tac-container">
                    <Checkbox
                        className="tac-checkbox"
                        onChange={tac.handleCheckboxInput}
                    >
                        <GbbTermsAndConditions/>
                    </Checkbox>
                    {tac.showError && tac.isError && (
                        <div>
                            <Notification notificationType="orange">
                                {youMustAcceptLabel}
                            </Notification>
                        </div>
                    )}
                </div>

                <Row justify className="buttons">
                    <button
                        type="submit"
                        className="btn btn--green btn--sign buttons--submit"
                    >
                        {loading ? (
                            <LoaderButton/>
                        ) : (
                            <>
                                {sendInquiryLabel}
                            </>
                        )}
                    </button>
                    <div style={isMobile ? {transform: "scale(0.8)", transformOrigin: "0 0"} : {}}>
                        <ReCAPTCHA
                            badge="inline"
                            sitekey={window.googleInvisibleCaptchaSiteKey}
                            ref={recaptchaRef}
                            size="invisible"
                        />
                    </div>
                </Row>
            </form>
            {!!responseStatus && responseStatus === 200 && (
                <MessageSent/>
            )}
            {(isNotEmpty(errors) || responseStatus === 500) && (
                <UnexpectedError/>
            )}
        </Portal>
    )
}

export default BusRentContactForm